import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  MessagesSquare,
  PlayCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useLearnArticle,
  useLearnArticles,
  useMarkArticleComplete,
  useMarkArticleIncomplete,
} from "@/hooks/learn/useLearnArticles";
import { useBrain } from "@/contexts/ui/brain";
import { LearnArticle } from "@/types/learn";

const ArticlePage = () => {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { openBrain } = useBrain();

  const { data: article, isLoading } = useLearnArticle(slug);
  const { data: allArticlesData } = useLearnArticles();
  const markComplete = useMarkArticleComplete();
  const markIncomplete = useMarkArticleIncomplete();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [slug]);

  // Find prev/next in the full ordered article sequence
  const { prev, next } = useMemo<{ prev: LearnArticle | null; next: LearnArticle | null }>(() => {
    const all = allArticlesData?.articles ?? [];
    if (!article || all.length === 0) return { prev: null, next: null };
    const sorted = [...all].sort((a, b) => a.order_index - b.order_index);
    const idx = sorted.findIndex((a) => a.slug === article.slug);
    if (idx < 0) return { prev: null, next: null };
    return {
      prev: idx > 0 ? sorted[idx - 1] : null,
      next: idx < sorted.length - 1 ? sorted[idx + 1] : null,
    };
  }, [article, allArticlesData]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] overflow-x-hidden">
        <div className="container mx-auto px-4 py-16 max-w-[1920px] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-genie-primary" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] overflow-x-hidden">
        <div className="container mx-auto px-4 py-16 max-w-[1920px]">
          <div className="max-w-2xl mx-auto rounded-2xl border-2 border-gray-200 bg-white p-12 text-center">
            <h2 className="text-xl font-semibold text-genie-text mb-2">Article not found</h2>
            <p className="text-sm text-gray-600 mb-6">It may have been moved or unpublished.</p>
            <Button
              onClick={() => navigate("/learn")}
              className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white"
            >
              Back to Learn
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isComplete = !!article.completed_at;
  const isMutating = markComplete.isPending || markIncomplete.isPending;

  const goNextIfAny = () => {
    if (next) {
      navigate(`/learn/articles/${next.slug}`);
    } else {
      navigate("/learn?tab=start");
    }
  };

  const handleCompleteAndContinue = async () => {
    if (!isComplete) {
      await markComplete.mutateAsync(article.id);
    }
    goNextIfAny();
  };

  const handleToggleComplete = () => {
    if (isComplete) markIncomplete.mutate(article.id);
    else markComplete.mutate(article.id);
  };

  const handleAskTutor = () => {
    openBrain(
      `I just read the "${article.title}" article. Can you walk me through it and answer any follow-up questions?`,
    );
  };

  return (
    <div className="min-h-screen bg-[#F9FAFC] overflow-x-hidden">
      <div className="container mx-auto px-4 py-8 max-w-[1920px]">
        <Button
          variant="ghost"
          onClick={() => navigate("/learn")}
          className="mb-4 -ml-3 text-gray-600 hover:text-genie-primary hover:bg-genie-primary/5"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Learn
        </Button>

        <article className="max-w-4xl mx-auto bg-white border-2 border-gray-200 rounded-2xl overflow-hidden shadow-sm relative">
          {/* Brand accent strip */}
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-genie-primary via-purple-500 to-genie-secondary" />

          {/* Header */}
          <header className="relative px-6 sm:px-10 pt-7 sm:pt-9 pb-6 border-b border-gray-100 bg-gradient-to-br from-genie-primary/5 via-white to-genie-secondary/5">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-genie-primary/10 to-genie-secondary/10 text-genie-primary capitalize">
                {article.category.replace(/-/g, " ")}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <Clock className="h-3.5 w-3.5" />
                {article.est_minutes} min read
              </span>
              {article.video_url && (
                <span className="inline-flex items-center gap-1 text-xs text-genie-secondary font-medium">
                  <PlayCircle className="h-3.5 w-3.5" />
                  Video included
                </span>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-genie-text leading-tight">
              {article.title}
            </h1>
            {article.summary && <p className="text-gray-600 mt-2 max-w-3xl">{article.summary}</p>}
          </header>

          {article.video_url && (
            <div className="aspect-video bg-black">
              <iframe
                src={article.video_url}
                title={article.title}
                className="w-full h-full"
                frameBorder={0}
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            </div>
          )}

          {/* Body */}
          <div
            className="px-6 sm:px-10 py-6 sm:py-7
              prose prose-sm prose-gray max-w-none
              prose-headings:font-bold
              prose-h2:text-base sm:prose-h2:text-lg prose-h2:mt-6 prose-h2:mb-2 prose-h2:first:mt-0
              prose-h2:text-genie-text prose-h2:relative prose-h2:pl-3
              prose-h2:before:absolute prose-h2:before:left-0 prose-h2:before:top-1 prose-h2:before:bottom-1
              prose-h2:before:w-1 prose-h2:before:rounded-full prose-h2:before:bg-gradient-to-b prose-h2:before:from-genie-primary prose-h2:before:to-genie-secondary
              prose-h3:text-sm sm:prose-h3:text-base prose-h3:text-genie-text prose-h3:mt-4 prose-h3:mb-1.5
              prose-p:text-gray-700 prose-p:leading-relaxed prose-p:my-2.5
              prose-a:text-genie-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-genie-text prose-strong:font-semibold
              prose-ul:my-2.5 prose-ol:my-2.5 prose-li:my-1 prose-li:text-gray-700 prose-li:leading-relaxed
              prose-ul:marker:text-genie-primary prose-ol:marker:text-genie-primary
              prose-code:text-genie-primary prose-code:bg-genie-primary/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-code:font-mono prose-code:text-xs"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {article.body_markdown ?? ""}
            </ReactMarkdown>
          </div>

          {/* Footer with prev/next */}
          <footer className="border-t border-gray-100 bg-gray-50/50">
            {/* Primary action row */}
            <div className="px-6 sm:px-10 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleCompleteAndContinue}
                  disabled={isMutating}
                  className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all"
                >
                  {isComplete ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {next ? "Continue" : "Back to Learn"}
                    </>
                  ) : (
                    <>
                      <Circle className="h-4 w-4 mr-2" />
                      {next ? "Mark complete and continue" : "Mark complete and finish"}
                    </>
                  )}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>

                {isComplete && (
                  <button
                    onClick={handleToggleComplete}
                    disabled={isMutating}
                    className="text-xs text-gray-500 hover:text-genie-primary underline-offset-2 hover:underline"
                  >
                    Unmark complete
                  </button>
                )}
              </div>

              <Button
                variant="ghost"
                onClick={handleAskTutor}
                className="text-genie-primary hover:bg-genie-primary/5 self-start sm:self-auto"
              >
                <MessagesSquare className="h-4 w-4 mr-2" />
                Still stuck? Ask the Tutor
              </Button>
            </div>

            {/* Prev / Next links */}
            {(prev || next) && (
              <div className="px-6 sm:px-10 py-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {prev ? (
                  <button
                    onClick={() => navigate(`/learn/articles/${prev.slug}`)}
                    className="group text-left p-3 sm:p-4 rounded-xl bg-white border-2 border-gray-200 hover:border-genie-primary/40 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Previous
                    </div>
                    <div className="text-sm font-medium text-genie-text group-hover:text-genie-primary transition-colors line-clamp-1">
                      {prev.title}
                    </div>
                  </button>
                ) : (
                  <div />
                )}
                {next ? (
                  <button
                    onClick={() => navigate(`/learn/articles/${next.slug}`)}
                    className="group text-right p-3 sm:p-4 rounded-xl bg-white border-2 border-gray-200 hover:border-genie-primary/40 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-center justify-end gap-2 text-xs text-gray-500 mb-1">
                      Next
                      <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                    <div className="text-sm font-medium text-genie-text group-hover:text-genie-primary transition-colors line-clamp-1">
                      {next.title}
                    </div>
                  </button>
                ) : (
                  <div />
                )}
              </div>
            )}
          </footer>
        </article>
      </div>
    </div>
  );
};

export default ArticlePage;
